import { Fragment, useEffect, useRef, useState } from "react";
import CustomerLoader from "../../../../../components/CustomLoader";
import { Child, Container, Header, LayerContent } from "../style";
import { TitleBar } from "../../../../../components/DynamicGrid/DynamicGrid.styles";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import {
  convertObjectToChartData,
  formatKnownStrings,
  getMetrics,
  lineOptions,
  reverseFormatKnownStrings,
} from "../formUtils";
import moment from "moment";
import { ChartContainer, NoData } from "../../style";
import { Line, getElementAtEvent } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Tooltip,
} from "chart.js";
import {
  extractDateString,
  getMonthDateRange,
} from "../../../../../utils/helpers/analyticsUtils";
import { useAnalyticsWrapper } from "../../../../../store/store";
import useAnalytics from "../../../../../hooks/useAnalytics";
import SectionModal from "./SectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { incidentLists } from "../../dummyData/colums";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip
);

const projectedTableCols: any[] = [
  {
    Header: "Title",
    accessor: "title",
  },
  {
    Header: "Value",
    accessor: "value",
  },
];

const ActualVsProjected = ({
  setClickedIncident,
  isFetchingSelectedIncident,
  shareTableData,
}: any) => {
  const lineRef = useRef<any>(null);
  const [tableData, setTableData] = useState<any>([]);
  const [grid, setGrid] = useState<any>([]);
  const [chartData, setChartData] = useState<any>([]);
  const [monthStart, setMonthStart] = useState("");
  const [monthEnd, setMonthEnd] = useState("");
  const [label, setLabel] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [actualVsProjectedDetails, setActualVsProjectedDetails] = useState<any>(
    []
  );

  const { startDate, endDate, fallFacility } = useAnalyticsWrapper(
    (state: { startDate: any; endDate: any; fallFacility: any }) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      fallFacility: state.fallFacility,
    })
  );

  const { getActualVsProjectedFall, getActualVsProjectedDetails } =
    useAnalytics({
      facility: fallFacility?.value || "",
      startDate: moment(startDate).format("yyyy-MM-DD"),
      endDate: moment(endDate).format("yyyy-MM-DD"),
      monthStart: monthStart ? moment(monthStart).format("yyyy-MM-DD") : null,
      monthEnd: monthEnd ? moment(monthEnd).format("yyyy-MM-DD") : null,
      label: label,
    });

  const {
    data: actualVsProjectedFallData,
    isFetching: isLoadingActualVsProjected,
  } = getActualVsProjectedFall;

  const { data: actualVsProjectedDetailsData, isFetching: isLoadingDetails } =
    getActualVsProjectedDetails;

  useEffect(() => {
    if (actualVsProjectedFallData !== undefined) {
      const tempData = actualVsProjectedFallData.data.data;
      const formatedTableData = getMetrics(tempData);
      formatedTableData.push({
        title: "Total Falls",
        value: tempData?.totalFall || 0,
      });
      const formatedChartData = convertObjectToChartData(tempData);
      console.log("FORMATED FALL CHART DATA", formatedChartData);
      setTableData(formatedTableData);
      setChartData(formatedChartData);
      shareTableData(formatedTableData);
    }
  }, [actualVsProjectedFallData]);

  useEffect(() => {
    if (actualVsProjectedDetailsData) {
      const tempDetails = actualVsProjectedDetailsData.data.data.map((item: any) => ({
        ...item,
      }));
      setActualVsProjectedDetails(tempDetails);
    }
  }, [actualVsProjectedDetailsData]);

  const title = "Fall Analysis";

  const handleChartClicked = (element: any) => {
    if (element) {
      const index = element.index;
      const datasetIndex = element.datasetIndex;

      const date = chartData?.labels[index];
      const { year } = extractDateString(date);

      const { start, end } = getMonthDateRange(date, year);

      const label = reverseFormatKnownStrings(
        chartData.datasets.map((data: any) => data.label)[datasetIndex]
      );

      setShowModal(true);
      setLabel(label);
      setMonthStart(start);
      setMonthEnd(end);
    }
  };

  useEffect(() => {
    let cellProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="none"
          rows={{ data: TotalRow }}
        >
          <GridColumn field="title" title="Type" />
          <GridColumn
            field="value"
            title="Value"
            key="value"
            {...(cellProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]);

  return (
    <LayerContent>
      {isLoadingActualVsProjected ? (
        <CustomerLoader />
      ) : (
        <Fragment>
          <Header>{title}</Header>
          <Container>
            <Child flex="35%">
              <TitleBar>{moment(startDate).format("DD MMM YYYY")} - {moment(endDate).format("DD MMM YYYY")}</TitleBar>
              {grid}
            </Child>
            <Child flex="65">
              <ChartContainer>
                {chartData?.labels?.length > 0 ? (
                  <Line
                    ref={lineRef}
                    data={chartData}
                    options={lineOptions}
                    onClick={(event) => {
                      const clickedElement = getElementAtEvent(
                        lineRef.current,
                        event
                      )[0];
                      handleChartClicked(clickedElement);
                    }}
                  />
                ) : (
                  <NoData>No Data</NoData>
                )}
              </ChartContainer>
            </Child>
          </Container>
        </Fragment>
      )}
      {showModal ? (
        <SectionModal
          open={showModal}
          facilityName={fallFacility?.label}
          startDate={monthStart}
          endDate={monthEnd}
          closeFn={() => setShowModal(false)}
          resetFn={() => {
            setMonthStart("");
            setMonthEnd("");
            setLabel("");
            setActualVsProjectedDetails(undefined);
          }}
          isLoading={isLoadingDetails || isFetchingSelectedIncident}
          tableData={actualVsProjectedDetails}
          title={`${title} - ${formatKnownStrings(label)}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: actualVsProjectedDetails,
              columnData: incidentLists(),
              fileName: fallFacility?.label,
              startDate: monthStart,
              endDate: monthEnd,
              subTitle: `${title} - ${formatKnownStrings(label)}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              actualVsProjectedDetails,
              incidentLists(),
              `${fallFacility?.label} - ${title} - ${formatKnownStrings(label)}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default ActualVsProjected;
